import {
  getCategoryIdFromItemId,
  getCategoryNameFromId,
  getSubcategoryIdFromItemId,
  getSubcategoryNameFromId,
} from "../../OnlineOrdering/Menu/helpers/getMenuData";

window.dataLayer = window.dataLayer || [];
const noopGtag = function () {};
window.gtag = window.gtag || noopGtag;
const ecommerceEvents = new Set([
  "view_item_list",
  "view_item",
  "add_to_cart",
  "remove_from_cart",
  "begin_checkout",
  "add_payment_info",
  "purchase",
  "addToGCCart",
  "removeFromGCCart",
]);

const trackAnalyticsEvent = (eventName, payload = {}) => {
  const analyticsConfig = window.__cws5Analytics;
  const hasExplicitAnalyticsConfig =
    !!analyticsConfig &&
    typeof analyticsConfig === "object" &&
    analyticsConfig.hasOwnProperty("hasGA") &&
    analyticsConfig.hasOwnProperty("hasGTM");

  const hasConfiguredGA =
    hasExplicitAnalyticsConfig && analyticsConfig.hasGA === true && typeof window.gtag === "function";
  const hasConfiguredGTM = hasExplicitAnalyticsConfig && analyticsConfig.hasGTM === true;

  if (hasConfiguredGA && window.gtag !== noopGtag) {
    window.gtag("event", eventName, payload);
    return;
  }

  if (hasExplicitAnalyticsConfig && !hasConfiguredGTM) {
    return;
  }

  if (!hasExplicitAnalyticsConfig && typeof window.gtag === "function" && window.gtag !== noopGtag) {
    window.gtag("event", eventName, payload);
    return;
  }

  const dataLayerPayload = ecommerceEvents.has(eventName)
    ? {
        event: eventName,
        ecommerce: { ...payload },
      }
    : { event: eventName, ...payload };

  window.dataLayer.push(dataLayerPayload);
};

const getActiveCurrency = () => {
  let currencyCode = null;
  const skin = sessionStorage.getItem("merchantSkinName");

  if (sessionStorage.getItem(skin + "__merchantConfig")) {
    const merchantConfig = JSON.parse(sessionStorage.getItem(skin + "__merchantConfig")).merchant;
    currencyCode = merchantConfig.I34;
  }

  return currencyCode;
};

/**
 * Measures product impressions and also tracks a standard
 * pageview for the tag configuration.
 * Product impressions are sent by pushing an impressions object
 * containing one or more impressionFieldObjects.
 * @param {Array} listOfItems list of item being viewed
 * @param {String} category A string representing the category id of the items.
 * @param {String} subcategory A string representing a the subcategory id of the items.
 */
export const onImpression = (listOfItems, category, subcategory, storeContext, orderMenu) => {
  window.dataLayer.push({ ecommerce: null });
  let impressions = [];
  listOfItems.forEach((item, index) => {
    impressions.push({
      item_name: item.name,
      item_id: item.id,
      price: Number(item.price),
      position: index + 1,
      item_category_id: category ? category : getCategoryIdFromItemId(item.id, orderMenu),
      item_subcategory_id: subcategory
        ? subcategory
        : getSubcategoryIdFromItemId(item.id, orderMenu),
      index: index,
      quantity: 1,
      location_id: storeContext ? storeContext.activeOrderStore.storeId : "",
      affiliation: storeContext ? storeContext.activeOrderStore.name : "",
      item_category: getCategoryNameFromId(category, orderMenu, item.id),
      item_subcategory: getSubcategoryNameFromId(subcategory, orderMenu, item.id),
    });
  });

  if (impressions.length > 0) {
    trackAnalyticsEvent("view_item_list", {
      currency: getActiveCurrency(),
      items: impressions,
    });
  }
};

/**
 * Call this function when a user clicks on a product link. This function uses the event
 * callback datalayer variable to handle navigation after the ecommerce data has been sent
 * to Google Analytics.
 * @param {Object} item An object representing a product.
 * @param {String} category A string representing the category id of the item.
 * @param {String} subcategory A string representing a the subcategory id of the item.
 */
export const onProductClick = (
  item,
  category,
  subcategory,
  orderMenu,
  storeContext,
  manifestContext,
) => {
  window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
  trackAnalyticsEvent("view_item", {
    currency: getActiveCurrency(),
    value: Number(item.price),
    items: [
      {
        item_name: item.name,
        item_id: item.id,
        price: Number(item.price),
        item_category_id: category ? category : getCategoryIdFromItemId(item.id, orderMenu),
        item_subcategory_id: subcategory
          ? subcategory
          : getSubcategoryIdFromItemId(item.id, orderMenu),
        item_category: getCategoryNameFromId(category, orderMenu, item.id),
        item_subcategory: getSubcategoryNameFromId(subcategory, orderMenu, item.id),
        quantity: 1,
        location_id: storeContext ? storeContext.activeOrderStore.storeId : "",
        affiliation: storeContext ? storeContext.activeOrderStore.name : "",
        item_brand: manifestContext ? manifestContext.name : "",
      },
    ],
  });
};

/**
 * Measure adding a product to a shopping cart by using an 'add' actionFieldObject
 */
export const onAddProductToCart = (
  item,
  category,
  subcategory,
  orderMenu,
  storeContext,
  manifestContext,
) => {
  window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
  trackAnalyticsEvent("add_to_cart", {
    currency: getActiveCurrency(),
    value: Number(item.price) * (item.quantity || 1),
    items: [
      {
        //  adding a product to a shopping cart.
        item_name: item.name,
        item_id: item.id,
        price: Number(item.price),
        quantity: item.quantity,
        location_id: storeContext ? storeContext.activeOrderStore.storeId : "",
        affiliation: storeContext ? storeContext.activeOrderStore.name : "",
        item_category: getCategoryNameFromId(category, orderMenu, item.id),
        item_subcategory: getSubcategoryNameFromId(subcategory, orderMenu, item.id),
        item_category_id: category ? category : getCategoryIdFromItemId(item.id, orderMenu),
        item_subcategory_id: subcategory
          ? subcategory
          : getSubcategoryIdFromItemId(item.id, orderMenu),
        item_brand: manifestContext ? manifestContext.name : "",
      },
    ],
  });
};

/**
 * Measure the removal of a product from a shopping cart.
 */
export const onRemoveProductFromCart = (
  item,
  category,
  subcategory,
  isMultipleItems,
  orderMenu,
  storeContext,
  manifestContext,
) => {
  let productList = [];
  let totalValue = 0;
  if (item) {
    if (isMultipleItems) {
      item.forEach((tempItem) => {
        productList.push({
          item_name: tempItem.name,
          item_id: tempItem.id,
          price: Number(tempItem.price),
          item_category_id: tempItem.category
            ? tempItem.category
            : getCategoryIdFromItemId(tempItem.id, orderMenu),
          item_subcategory_id: tempItem.subcategory
            ? tempItem.subcategory
            : getSubcategoryIdFromItemId(tempItem.id, orderMenu),
          item_category: getCategoryNameFromId(tempItem.category, orderMenu, tempItem.id),
          item_subcategory: getSubcategoryNameFromId(tempItem.subcategory, orderMenu, tempItem.id),
          location_id: storeContext ? storeContext.activeOrderStore.storeId : "",
          affiliation: storeContext ? storeContext.activeOrderStore.name : "",
          item_brand: manifestContext ? manifestContext.name : "",
          quantity: tempItem.quantity || 1,
        });
        totalValue += Number(tempItem.price) * (tempItem.quantity || 1);
      });
    } else {
      productList.push({
        item_name: item.name,
        item_id: item.id,
        price: Number(item.price),
        item_category_id: item.category
          ? item.category
          : getCategoryIdFromItemId(item.id, orderMenu),
        item_subcategory_id: subcategory
          ? subcategory
          : getSubcategoryIdFromItemId(item.id, orderMenu),
        item_category: getCategoryNameFromId(category, orderMenu, item.id),
        item_subcategory: getSubcategoryNameFromId(subcategory, orderMenu, item.id),
        location_id: storeContext ? storeContext.activeOrderStore.storeId : "",
        affiliation: storeContext ? storeContext.activeOrderStore.name : "",
        item_brand: manifestContext ? manifestContext.name : "",
        quantity: item.quantity || 1,
      });
      totalValue = Number(item.price) * (item.quantity || 1);
    }

    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    trackAnalyticsEvent("remove_from_cart", {
      currency: getActiveCurrency(),
      value: totalValue,
      items: productList,
    });
  }
};

export const onAddProductToGCCart = (item, category) => {
  window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
  trackAnalyticsEvent("addToGCCart", {
    items: [
      {
        //  adding a product to a shopping cart.
        item_name: item.giftCard ? item.giftCard.title : "",
        item_id: item.giftCard ? item.giftCard.id : "",
        price: item.amount,
        category: "category",
        quantity: item.quantity,
      },
    ],
  });
};
export const onRemoveProductFromGCCart = (item, category, subcategory, isMultipleItems) => {
  let productList = [];
  if (item) {
    if (isMultipleItems) {
      item.forEach((tempItem) => {
        productList.push({
          item_name: tempItem.giftCard ? tempItem.giftCard.title : "",
          item_id: tempItem.giftCard ? tempItem.giftCard.id : "",
          amount: tempItem.amount,
          category: "category",
          quantity: tempItem.quantity,
        });
      });
    } else {
      productList.push({
        item_name: item.giftCard ? item.giftCard.title : "",
        item_id: item.giftCard ? item.giftCard.id : "",
        amount: item.amount,
        quantity: item.quantity,
      });
    }

    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    trackAnalyticsEvent("removeFromGCCart", {
      items: productList,
    });
  }
};

/**
 * A function to handle a click on a checkout button. This function uses the eventCallback
 * data layer variable to handle navigation after the ecommerce data has been sent to Google Analytics.
 */
export const onCheckout = (
  items,
  stepNumber,
  paymentType = "",
  orderSummary,
  orderMenu,
  storeContext,
  manifestContext,
  coupon,
  promoCode,
) => {
  if (items === null || !items) {
    return;
  }
  window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
  let products = [];
  let totalValue = 0;

  items.forEach((item) => {
    products.push({
      item_name: item.name,
      item_id: item.id,
      price: Number(item.price),
      quantity: item.quantity,
      location_id: storeContext ? storeContext.activeOrderStore.storeId : "",
      affiliation: storeContext ? storeContext.activeOrderStore.name : "",
      item_category_id: item.category ? item.category : getCategoryIdFromItemId(item.id, orderMenu),
      item_subcategory_id: item.subcategory
        ? item.subcategory
        : getSubcategoryIdFromItemId(item.id, orderMenu),
      item_brand: manifestContext ? manifestContext.name : "",
      item_category: getCategoryNameFromId(item.category, orderMenu, item.id),
      item_subcategory: getSubcategoryNameFromId(item.subcategory, orderMenu, item.id),
    });
    totalValue += Number(item.price) * (item.quantity || 1);
  });

  trackAnalyticsEvent("begin_checkout", {
    currency: getActiveCurrency(),
    coupon: coupon || "",
    discount: promoCode || "",
    value: totalValue,
    option: paymentType,
    total_discount: orderSummary ? orderSummary.discountTotal : "",
    items: products,
    service_charge_amount:
      orderSummary && !isNaN(orderSummary.serviceChargeAmount)
        ? orderSummary.serviceChargeAmount
        : "",
    service_charge_description:
      orderSummary && orderSummary.serviceChargeDesc ? orderSummary.serviceChargeDesc : "",
    tax: orderSummary && !isNaN(orderSummary.taxTotal) ? Number(orderSummary.taxTotal) : "",
    tip: orderSummary && !isNaN(orderSummary.tipAmount) ? Number(orderSummary.tipAmount) : "",
  });
};

export const onPaymentInfoAdded = (
  items,
  paymentType,
  coupon,
  promoCode,
  orderSummary,
  orderMenu,
  storeContext,
  manifestContext,
) => {
  let products = [];
  let totalValue = 0;

  items.forEach((item) => {
    products.push({
      item_name: item.name,
      item_id: item.id,
      price: Number(item.price),
      quantity: item.quantity,
      location_id: storeContext ? storeContext.activeOrderStore.storeId : "",
      affiliation: storeContext ? storeContext.activeOrderStore.name : "",
      item_category_id: item.category ? item.category : getCategoryIdFromItemId(item.id, orderMenu),
      item_subcategory_id: item.subcategory
        ? item.subcategory
        : getSubcategoryIdFromItemId(item.id, orderMenu),
      item_brand: manifestContext ? manifestContext.name : "",
      item_category: getCategoryNameFromId(item.category, orderMenu, item.id),
      item_subcategory: getSubcategoryNameFromId(item.subcategory, orderMenu, item.id),
    });
    totalValue += Number(item.price) * (item.quantity || 1);
  });

  window.dataLayer.push({ ecommerce: null });
  trackAnalyticsEvent("add_payment_info", {
    currency: getActiveCurrency(),
    value: totalValue,
    coupon: coupon || "",
    discount: promoCode || "",
    tax: orderSummary ? Number(orderSummary.I4) : 0,
    payment_type: paymentType || "",
    items: products,
  });
};

/**
 * Send transaction data with a pageview if available
 * when the page loads. Otherwise, use an event when the transaction
 * data becomes available.
 * @param {Array} cartItems list of purchased items
 * @param {Object} orderSummary details of the order (total, tax etc)
 * @param {Object} orderStore details of the selected store
 */
export const onPurchaseComplete = (
  cartItems,
  orderSummary,
  orderStore,
  onlineOrderId,
  purchaseAdditionalInfo,
  orderMenu,
  manifestContext,
) => {
  window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
  let products = [];
  let totalValue = 0;
  cartItems.forEach((item) => {
    products.push({
      item_name: item.name,
      item_id: item.id,
      price: Number(item.price),
      item_category_id: item.category ? item.category : getCategoryIdFromItemId(item.id, orderMenu),
      item_subcategory_id: item.subcategory
        ? item.subcategory
        : getSubcategoryIdFromItemId(item.id, orderMenu),
      item_category: getCategoryNameFromId(item.category, orderMenu, item.id),
      item_subcategory: getSubcategoryNameFromId(item.subcategory, orderMenu, item.id),
      item_brand: manifestContext ? manifestContext.name : "",
      location_id: orderStore ? orderStore.storeId : "",
      affiliation: orderStore ? orderStore.name : "",
      quantity: item.quantity,
    });
    totalValue += Number(item.price) * (item.quantity || 1);
  });

  trackAnalyticsEvent("purchase", {
    transaction_id: onlineOrderId, // Transaction ID. Required for purchases and refunds.
    value: totalValue, // Total transaction value (not incl. tax and shipping)
    tax: Number(orderSummary.I4),
    currency: getActiveCurrency(),
    coupon: purchaseAdditionalInfo.coupon,
    items: products,
    total_discount: orderSummary.I2.discount_total,
    order_type: purchaseAdditionalInfo.orderType,
    discount: purchaseAdditionalInfo.promoCode,
    rewards: purchaseAdditionalInfo.rewards,
    tip: purchaseAdditionalInfo.tip,
    points_issued: purchaseAdditionalInfo.pointsIssued,
    affiliation: orderStore.name,
  });
};
